import React from 'react';
import { Text, Flex, Box, Select, Checkbox } from '@scale/scaleui-radix';
import {
  CompassCardComponent,
  CompassCardComponentProps,
} from 'react-scale/corp/views/DataCompassV2/types';
import useUpdateProjectAggThrottleCard from './useUpdateProjectAggThrottleCard';

const UpdateProjectAggThrottleCardComponent: CompassCardComponent = ({
  index,
}: CompassCardComponentProps) => {
  const {
    columnIdWithProjectId,
    columnIdWithThrottleEntryId,
    columnIdWithThreshold,
    dryRun,
    setColumnIdWithProjectId,
    setColumnIdWithThrottleEntryId,
    setColumnIdWithThreshold,
    setDryRun,
    availableColumns,
  } = useUpdateProjectAggThrottleCard(index);

  return (
    <Flex direction="column" gap="4">
      <Text size="2" weight="medium">
        Update the maxAttempts threshold on a project-aggregate throttle entry. Select a column from
        the previous card for each field.
      </Text>

      {/* Project ID Column */}
      <Box>
        <Flex direction="column" gap="1">
          <Flex direction="row" gap="1">
            <Text size="2" weight="medium">
              Project ID Column
            </Text>
            <Text size="2" color="red">
              *
            </Text>
          </Flex>
          <Select.Root value={columnIdWithProjectId} onValueChange={setColumnIdWithProjectId}>
            <Select.Trigger className="w-full" placeholder="Select column for project ID" />
            <Select.Content>
              {availableColumns.map(column => (
                <Select.Item key={column.id ?? column.header} value={column.id ?? ''}>
                  {column.header ?? column.id}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Flex>
      </Box>

      {/* Throttle Entry ID Column */}
      <Box>
        <Flex direction="column" gap="1">
          <Flex direction="row" gap="1">
            <Text size="2" weight="medium">
              Throttle Entry ID Column
            </Text>
            <Text size="2" color="red">
              *
            </Text>
          </Flex>
          <Select.Root
            value={columnIdWithThrottleEntryId}
            onValueChange={setColumnIdWithThrottleEntryId}
          >
            <Select.Trigger className="w-full" placeholder="Select column for throttle entry ID" />
            <Select.Content>
              {availableColumns.map(column => (
                <Select.Item key={column.id ?? column.header} value={column.id ?? ''}>
                  {column.header ?? column.id}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Flex>
      </Box>

      {/* Threshold Column */}
      <Box>
        <Flex direction="column" gap="1">
          <Flex direction="row" gap="1">
            <Text size="2" weight="medium">
              Threshold Column
            </Text>
            <Text size="2" color="red">
              *
            </Text>
          </Flex>
          <Select.Root value={columnIdWithThreshold} onValueChange={setColumnIdWithThreshold}>
            <Select.Trigger className="w-full" placeholder="Select column for threshold" />
            <Select.Content>
              {availableColumns.map(column => (
                <Select.Item key={column.id ?? column.header} value={column.id ?? ''}>
                  {column.header ?? column.id}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Flex>
      </Box>

      {/* Dry Run */}
      <Flex align="start" gap="2">
        <Checkbox checked={dryRun} onCheckedChange={setDryRun} />
        <Text size="2">
          Dry Run Mode. Validate and preview changes without writing to the database.
        </Text>
      </Flex>
    </Flex>
  );
};

export default UpdateProjectAggThrottleCardComponent;
